/**
 * Componente para exibir mensagens de carrossel interativo no chat
 * 
 * Renderiza cartões deslizantes estilo WhatsApp com:
 * - Header com título do carrossel
 * - Slider horizontal usando Embla Carousel
 * - Cartões com imagem, texto e botões
 * - Navegação por setas e indicadores (dots)
 * 
 * @version 1.0.0
 */

import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import { 
  ExternalLink, 
  MessageSquare, 
  Phone,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Loader2 
} from 'lucide-react';

// ============================================================================
// INTERFACES
// ============================================================================

/** Estrutura de um botão do cartão do carrossel */
interface CarouselButton {
  type: 'URL' | 'REPLY' | 'CALL' | string;
  label: string;
  url?: string;
  phone?: string;
}

/** Estrutura de um cartão do carrossel */
export interface CarouselCard {
  index: number;
  imageUrl: string;
  text: string;
  buttons: CarouselButton[];
}

/** Props do componente CarouselMessageBubble */
export interface CarouselMessageBubbleProps {
  /** Título/legenda do carrossel */
  title: string;
  /** Lista de cartões do carrossel */
  cards: CarouselCard[];
  /** Direção da mensagem (inbound/outbound) */
  direction: 'inbound' | 'outbound';
  /** Callback ao clicar em imagem para abrir galeria */
  onOpenGallery?: (index: number) => void;
}

// ============================================================================
// COMPONENTE DE CARTÃO INDIVIDUAL
// ============================================================================

interface CarouselCardItemProps {
  card: CarouselCard;
  direction: 'inbound' | 'outbound';
  onImageClick?: () => void;
}

function CarouselCardItem({ card, direction, onImageClick }: CarouselCardItemProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  // Converte caminho relativo para URL completa se necessário
  const imageUrl = getStorageUrl(card.imageUrl) || card.imageUrl;

  const handleImageLoad = () => setIsLoading(false);
  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Renderiza ícone baseado no tipo de botão
  const getButtonIcon = (type: string) => {
    switch (type.toUpperCase()) {
      case 'URL':
        return <ExternalLink className="h-3 w-3" />;
      case 'CALL':
        return <Phone className="h-3 w-3" />;
      case 'REPLY':
      default:
        return <MessageSquare className="h-3 w-3" />;
    }
  };

  // Handler para clique em botão
  const handleButtonClick = (button: CarouselButton) => {
    if (button.type.toUpperCase() === 'URL' && button.url) {
      window.open(button.url, '_blank', 'noopener,noreferrer');
    } else if (button.type.toUpperCase() === 'CALL' && button.phone) {
      window.open(`tel:${button.phone}`, '_self');
    }
    // REPLY não tem ação - apenas visual
  };

  return (
    <div 
      className={cn(
        'flex flex-col rounded-lg overflow-hidden border shadow-sm',
        'w-[180px] flex-shrink-0',
        direction === 'outbound' 
          ? 'bg-[hsl(var(--chat-bubble-outgoing))] border-[hsl(var(--chat-bubble-outgoing))]/50'
          : 'bg-card border-border/30'
      )}
    >
      {/* Área da imagem */}
      <div 
        className="relative aspect-square cursor-pointer bg-muted/30"
        onClick={onImageClick}
      >
        {/* Loading state */}
        {isLoading && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        
        {/* Error state */}
        {hasError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/50">
            <ImageOff className="h-8 w-8 text-muted-foreground mb-1" />
            <span className="text-xs text-muted-foreground">Imagem indisponível</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={card.text || `Cartão ${card.index + 1}`}
            className={cn(
              'w-full h-full object-cover transition-opacity',
              isLoading ? 'opacity-0' : 'opacity-100'
            )}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
      </div>

      {/* Área de texto */}
      {card.text && (
        <div className="p-2.5">
          <p 
            className={cn(
              'text-xs leading-relaxed line-clamp-3',
              direction === 'outbound' 
                ? 'text-[hsl(var(--chat-text-primary))]'
                : 'text-foreground'
            )}
          >
            {card.text}
          </p>
        </div>
      )}

      {/* Área de botões */}
      {card.buttons && card.buttons.length > 0 && (
        <div className="border-t border-border/20 divide-y divide-border/20">
          {card.buttons.map((button, btnIdx) => (
            <button
              key={btnIdx}
              onClick={() => handleButtonClick(button)}
              className={cn(
                'w-full flex items-center justify-center gap-1.5 py-2 px-2',
                'text-xs font-medium transition-colors',
                'text-[#00a884] hover:bg-[#00a884]/10',
                button.type.toUpperCase() === 'REPLY' && 'cursor-default hover:bg-transparent'
              )}
            >
              {getButtonIcon(button.type)}
              <span className="truncate">{button.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function CarouselMessageBubble({
  title,
  cards,
  direction,
  onOpenGallery,
}: CarouselMessageBubbleProps) {
  // Configuração do Embla Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });

  // Estados para navegação
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Atualiza estados de navegação
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Navegação
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // Se não há cartões, não renderiza nada
  if (!cards || cards.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 max-w-[400px] -mx-2.5 md:-mx-4">
      {/* Título do carrossel */}
      {title && (
        <p 
          className={cn(
            'text-sm font-medium px-1',
            direction === 'outbound' 
              ? 'text-[hsl(var(--chat-text-primary))]'
              : 'text-foreground'
          )}
        >
          {title}
        </p>
      )}

      {/* Container do carrossel */}
      <div className="relative">
        {/* Viewport do carrossel */}
        <div ref={emblaRef} className="overflow-hidden rounded-lg">
          <div className="flex gap-2">
            {cards.map((card, index) => (
              <CarouselCardItem
                key={index}
                card={card}
                direction={direction}
                onImageClick={() => onOpenGallery?.(index)}
              />
            ))}
          </div>
        </div>

        {/* Botão anterior */}
        {canScrollPrev && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute left-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full shadow-md bg-background/90 hover:bg-background"
            onClick={scrollPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Botão próximo */}
        {canScrollNext && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full shadow-md bg-background/90 hover:bg-background"
            onClick={scrollNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Indicadores (dots) - apenas se mais de 1 cartão */}
      {cards.length > 1 && (
        <div className="flex justify-center gap-1.5 pt-1">
          {cards.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className={cn(
                'w-1.5 h-1.5 rounded-full transition-all',
                index === selectedIndex
                  ? 'bg-[#00a884] w-3'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              )}
              aria-label={`Ir para cartão ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
